import { Button, Checkbox, CircularProgress, CircularProgressSize, PopUp, SwitchToggle, theme } from '@ketch-com/deck'
import { SubscriptionStatus, SubscriptionTopicSetting } from '@ketch-sdk/ketch-types'
import { Box, styled, Typography } from '@mui/material'
import { useSubscriptionTopic } from 'api/subscriptions/queries/useSubscriptionTopic'
import { ContactMethod, ContactMethodLabels } from 'pages/insights/auditLog/constants'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import React, { useEffect, useMemo } from 'react'

export enum AuditLogActionVariant {
  AcceptAllConsent = 'acceptAllConsent',
  RejectAllConsent = 'rejectAllConsent',
  SetAllDefaultConsent = 'setDefaultConsent',
  AcceptSingleConsent = 'acceptSingleConsent',
  RejectSingleConsent = 'rejectSingleConsent',
  SetDefaultSingleConsent = 'setDefaultSingleConsent',
  SubscribeAllSubscriptions = 'subscribeAllSubscriptions',
  UnsubscribeAllSubscriptions = 'unsubscribeAllSubscriptions',
  SetAllDefaultSubscriptions = 'setDefaultSubscription',
  ManageSingleSubscription = 'manageSingleSubscription',
}

const actionVariantTitle: Record<AuditLogActionVariant, string> = {
  [AuditLogActionVariant.AcceptAllConsent]: 'Consent Choices Update',
  [AuditLogActionVariant.RejectAllConsent]: 'Consent Choices Update',
  [AuditLogActionVariant.SetAllDefaultConsent]: 'Consent Choices Update',
  [AuditLogActionVariant.AcceptSingleConsent]: 'Consent Choice Update',
  [AuditLogActionVariant.RejectSingleConsent]: 'Consent Choice Update',
  [AuditLogActionVariant.SetDefaultSingleConsent]: 'Consent Choice Update',
  [AuditLogActionVariant.SubscribeAllSubscriptions]: 'Subscription Choices Update',
  [AuditLogActionVariant.UnsubscribeAllSubscriptions]: 'Subscription Choices Update',
  [AuditLogActionVariant.SetAllDefaultSubscriptions]: 'Subscription Choices Update',
  [AuditLogActionVariant.ManageSingleSubscription]: 'Manage Subscription',
}

const ManageSubscriptionBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  width: '648px',
  background: theme.palette.Black.o8,
  margin: '14px 0px',
})

const ManageSubscriptionBlock: React.FC = () => {
  const {
    auditLogActionSubscriptionCode,
    auditLogActionSubscriptionContactMethods,
    setAuditLogActionSubscriptionContactMethods,
    subscriptionsData,
  } = useAuditLogContext()

  const { data: topicResponse, isLoading: isLoadingTopic } = useSubscriptionTopic({
    enabled: !!auditLogActionSubscriptionCode,
    params: { code: auditLogActionSubscriptionCode || '' },
  })

  const { topic: enrichedTopic } = topicResponse || {}

  useEffect(() => {
    // Find the current status values for this topic
    const currentStatusSettings: SubscriptionTopicSetting = subscriptionsData?.reduce((acc, topic) => {
      if (topic.length > 0 && topic[0].code === auditLogActionSubscriptionCode) {
        return topic.reduce((channelAcc, channel) => {
          if (channel.contactMethod && enrichedTopic?.contactMethods.includes(channel.contactMethod)) {
            channelAcc[channel.contactMethod as ContactMethod] = {
              status: channel.allowed ? SubscriptionStatus.Granted : SubscriptionStatus.Denied,
            }
          }
          return channelAcc
        }, acc)
      }
      return acc
    }, {} as SubscriptionTopicSetting)

    setAuditLogActionSubscriptionContactMethods(currentStatusSettings)
  }, [
    enrichedTopic?.contactMethods,
    setAuditLogActionSubscriptionContactMethods,
    auditLogActionSubscriptionCode,
    subscriptionsData,
  ])

  if (isLoadingTopic)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="153px" width="100%">
        <CircularProgress size={CircularProgressSize.large} />
      </Box>
    )

  if (!topicResponse) return null

  return (
    <ManageSubscriptionBox>
      <Box display="flex" justifyContent="space-between" p="12px 12px 24px 16px">
        <Box display="flex" flexDirection="column" gap="4px" justifyContent="center">
          <Typography fontSize={16} fontWeight={600}>
            {enrichedTopic?.name}
          </Typography>
          {enrichedTopic?.description && <Typography variant="body">{enrichedTopic.description}</Typography>}
        </Box>
        <SwitchToggle
          size="medium"
          onChange={(e, checked) => {
            if (enrichedTopic) {
              setAuditLogActionSubscriptionContactMethods(
                enrichedTopic?.contactMethods.reduce((acc, contactMethod) => {
                  acc[contactMethod as ContactMethod] = {
                    status: checked ? SubscriptionStatus.Granted : SubscriptionStatus.Denied,
                  }
                  return acc
                }, {} as SubscriptionTopicSetting),
              )
            }
          }}
          checked={Object.values(auditLogActionSubscriptionContactMethods || {}).some(
            contactMethod => contactMethod.status === SubscriptionStatus.Granted,
          )}
        />
      </Box>
      <Box display="flex" p="0px 16px 7px 7px" mt="-1px" gap="32px">
        {enrichedTopic?.contactMethods?.map(contactMethod => (
          <Box display="inline-flex" alignItems="center" key={`${enrichedTopic.code}-${contactMethod}`}>
            <Checkbox
              checked={
                !!auditLogActionSubscriptionContactMethods &&
                auditLogActionSubscriptionContactMethods[contactMethod as ContactMethod]?.status ===
                  SubscriptionStatus.Granted
              }
              onChange={() => {
                setAuditLogActionSubscriptionContactMethods(prev => {
                  if (prev) {
                    return {
                      ...prev,
                      [contactMethod as ContactMethod]: {
                        status:
                          prev[contactMethod as ContactMethod]?.status === SubscriptionStatus.Granted
                            ? SubscriptionStatus.Denied
                            : SubscriptionStatus.Granted,
                      },
                    }
                  }
                  return {
                    [contactMethod as ContactMethod]: {
                      status: SubscriptionStatus.Granted,
                    },
                  }
                })
              }}
              // sx={{ pl: 0, ml: '-9px' }}
            />
            <Typography variant="label">{ContactMethodLabels[contactMethod as ContactMethod]}</Typography>
          </Box>
        ))}
      </Box>
    </ManageSubscriptionBox>
  )
}

export const AuditLogPopup: React.FC = () => {
  const {
    auditLogActionVariant,
    auditLogActionPurposeCode,
    closeAuditLogPopup,
    confirmAuditLogPopup,
    queries,
    isUpdating,
  } = useAuditLogContext()

  const actionVariantDescription: Record<AuditLogActionVariant, React.ReactNode> = useMemo(
    () => ({
      [AuditLogActionVariant.AcceptAllConsent]: (
        <Typography>
          You want to set all consent choices to{' '}
          <Typography component="span" fontWeight={700}>
            Opt In
          </Typography>{' '}
          for{' '}
          <Typography component="span" fontWeight={700}>
            {queries.identifierSearch}
          </Typography>
          .
        </Typography>
      ),
      [AuditLogActionVariant.RejectAllConsent]: (
        <Typography>
          You want to set all consent choices to{' '}
          <Typography component="span" fontWeight={700}>
            Opt Out
          </Typography>{' '}
          for{' '}
          <Typography component="span" fontWeight={700}>
            {queries.identifierSearch}
          </Typography>
          .
        </Typography>
      ),
      [AuditLogActionVariant.SetAllDefaultConsent]: (
        <Typography>
          You want to set all consent choices to their{' '}
          <Typography component="span" fontWeight={700}>
            Default Configurations
          </Typography>{' '}
          for{' '}
          <Typography component="span" fontWeight={700}>
            {queries.identifierSearch}
          </Typography>
          .
        </Typography>
      ),
      [AuditLogActionVariant.AcceptSingleConsent]: (
        <Typography>
          This will set the consent choice for{' '}
          <Typography component="span" fontWeight={700}>
            {auditLogActionPurposeCode}
          </Typography>{' '}
          to{' '}
          <Typography component="span" fontWeight={700}>
            Opted-In
          </Typography>{' '}
          for{' '}
          <Typography component="span" fontWeight={700}>
            {queries.identifierSearch}
          </Typography>
          .
        </Typography>
      ),
      [AuditLogActionVariant.SetDefaultSingleConsent]: (
        <Typography>
          This will set the consent choice for{' '}
          <Typography component="span" fontWeight={700}>
            {auditLogActionPurposeCode}
          </Typography>{' '}
          to it's{' '}
          <Typography component="span" fontWeight={700}>
            Default Configuration
          </Typography>{' '}
          for{' '}
          <Typography component="span" fontWeight={700}>
            {queries.identifierSearch}
          </Typography>
          .
        </Typography>
      ),
      [AuditLogActionVariant.RejectSingleConsent]: (
        <Typography>
          This will set the consent choice for{' '}
          <Typography component="span" fontWeight={700}>
            {auditLogActionPurposeCode}
          </Typography>{' '}
          to{' '}
          <Typography component="span" fontWeight={700}>
            Opted-Out
          </Typography>{' '}
          for{' '}
          <Typography component="span" fontWeight={700}>
            {queries.identifierSearch}
          </Typography>
          .
        </Typography>
      ),
      [AuditLogActionVariant.SubscribeAllSubscriptions]: (
        <Typography>
          You want to set all subscription topics to{' '}
          <Typography component="span" fontWeight={700}>
            Subscribed
          </Typography>{' '}
          for{' '}
          <Typography component="span" fontWeight={700}>
            {queries.identifierSearch}
          </Typography>{' '}
          with all contact methods set to{' '}
          <Typography component="span" fontWeight={700}>
            Enabled
          </Typography>
          .
        </Typography>
      ),
      [AuditLogActionVariant.UnsubscribeAllSubscriptions]: (
        <Typography>
          You want to set all subscription topics to{' '}
          <Typography component="span" fontWeight={700}>
            Unsubscribed
          </Typography>{' '}
          for{' '}
          <Typography component="span" fontWeight={700}>
            {queries.identifierSearch}
          </Typography>{' '}
          with all contact methods set to{' '}
          <Typography component="span" fontWeight={700}>
            Disabled
          </Typography>
          .
        </Typography>
      ),
      [AuditLogActionVariant.SetAllDefaultSubscriptions]: (
        <Typography>
          You want to set all subscription topics to their{' '}
          <Typography component="span" fontWeight={700}>
            Default Configurations
          </Typography>{' '}
          for{' '}
          <Typography component="span" fontWeight={700}>
            {queries.identifierSearch}
          </Typography>
          .
        </Typography>
      ),
      [AuditLogActionVariant.ManageSingleSubscription]: <ManageSubscriptionBlock />,
    }),
    [queries.identifierSearch, auditLogActionPurposeCode],
  )

  if (!auditLogActionVariant) return null

  const isManageSingleSubscription = auditLogActionVariant === AuditLogActionVariant.ManageSingleSubscription

  return (
    <PopUp
      title={actionVariantTitle[auditLogActionVariant]}
      variant={isManageSingleSubscription ? 'modal' : 'dialog'}
      onClose={closeAuditLogPopup}
      popUpActionChildren={
        <>
          <Button size="large" color="secondary" onClick={closeAuditLogPopup} pending={isUpdating}>
            Close
          </Button>
          <Button size="large" onClick={confirmAuditLogPopup} pending={isUpdating}>
            Confirm
          </Button>
        </>
      }
    >
      {actionVariantDescription[auditLogActionVariant]}
      {!isManageSingleSubscription && <Typography>Please confirm this action.</Typography>}
    </PopUp>
  )
}
